import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { MyrAPIService } from '../../../api/myr/myrAPI';
import { IReduxRootState } from '../../../app/reduxRootState.interface';
import { ErrorResolution } from '../../../app/common/errorResolution/errorResolution';
import { ErrorResolutionActions } from '../../../app/common/errorResolution/errorResolutionActions';
import { NotificationsActions } from '../../../app/common/notifications/notificationsActions';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import AccountSecurityActions from '../accountSecurityActions';
import { UpdatePasswordAction, UpdatePasswordActionType } from './updatePasswordActionType';
import { IUpdatePasswordRequest } from './updatePasswordRequest';
import { MyrAPIEndpoints } from '../../../api/myr/myrAPIEndpoints.constant';
import { oauthAPIService } from '../../../api/oauth/oauthAPI';
import { RevokeSessionActionTypes } from '../revokeSession/revokeSessionActionTypes';
import ErrorRedirectService from '../../../routes/error/errorRedirect.service';
import { NOTIFICATIONS } from '../../../app/common/constants/styles';
import { NotificationType } from '../../../app/common/notifications/notificationType.enum';
import { RevokeSessionService } from '../revokeSession/revokeSession.services';
import { addRatEvent } from '../../../utils/ratClickEvents';

export class UpdatePasswordActions {
    public static updateMemberPassword(
        data: IUpdatePasswordRequest,
        signOutFromAllDevices: boolean,
    ): ThunkAction<void, IReduxRootState, {}, AnyAction> {
        return async (
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            getState: () => IReduxRootState,
        ) => {
            try {
                dispatch(request());
                await MyrAPIService.sendRequest(MyrAPIEndpoints.PASSWORD, 'POST', data);
                if (signOutFromAllDevices) {
                    await handleSessionRevocation(dispatch, getState);
                } else {
                    success(dispatch);
                    await oauthAPIService.updateAccessToken();
                }
            } catch (error) {
                const errorResolution = error as ErrorResolution;
                return failure(dispatch, errorResolution);
            }
        };

        async function handleSessionRevocation(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            getState: () => IReduxRootState,
        ) {
            try {
                dispatch({ type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_REQUEST });
                const sessionRevokeRespone = await RevokeSessionService.requestSessionRevoke();
                if (!!sessionRevokeRespone) {
                    const tokenExpirationUrl = getState().app.clientConfig.tokenExpirationUrl;
                    successWithSessionRevoke(dispatch, tokenExpirationUrl);
                }
            } catch (error) {
                const errorResolution = error as ErrorResolution;
                success(dispatch);
                dispatch({
                    type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_FAILURE,
                    payload: { errorResolution: errorResolution },
                });
            } // show success notification for password update & failed text for session revocation
        }

        function request(): UpdatePasswordAction {
            return { type: UpdatePasswordActionType.UPDATE_PASSWORD_REQUEST, payload: {} };
        }

        function success(dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>) {
            addRatEvent('password_update', 'password_edit_success');
            dispatch({ type: UpdatePasswordActionType.UPDATE_PASSWORD_SUCCESS, payload: {} });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage(
                        'notifications.account.password.update.success',
                    ),
                ),
            );
            dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
        }

        function successWithSessionRevoke(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            tokenExpirationUrl: string,
        ) {
            dispatch({ type: UpdatePasswordActionType.UPDATE_PASSWORD_SUCCESS, payload: {} });
            dispatch({
                type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_SUCCESS,
            });
            dispatch(
                NotificationsActions.showRedirectSuccess(
                    'notification.revoke.session.success.password.update.success',
                ),
            );
            dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
            // redirect the user to login page after session revocation
            setInterval(() => {
                ErrorRedirectService.takeToLogout(tokenExpirationUrl.toString());
            }, NOTIFICATIONS[NotificationType.Success_Redirecting].expiry);
        }

        function failure(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            errorResolution: ErrorResolution,
        ) {
            dispatch({
                payload: { errorResolution },
                type: UpdatePasswordActionType.UPDATE_PASSWORD_FAILURE,
            });
            dispatch(
                ErrorResolutionActions.processErrorResolution(
                    errorResolution,
                    'notifications.account.password.update.failure',
                    { challengerPid: data.challengeData.cpid },
                ),
            );
        }
    }
}
